import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { notificationsAPI } from '../services/api';
import { getTenant, getUser, isAuthenticated } from '../services/auth';

const NotificationsContext = createContext(null);

const parsePayload = (response) => {
  const raw = response?.data ?? {};
  if (raw && typeof raw.value === 'object' && raw.value !== null) {
    return raw.value;
  }
  if (typeof raw?.value === 'string') {
    try {
      return JSON.parse(raw.value);
    } catch (_error) {
      return {};
    }
  }
  return raw;
};

const normalizeNotification = (item = {}) => ({
  id: String(item.notificationId || item.id || '').trim(),
  scope: String(item.scope || 'BROADCAST').trim().toLowerCase(),
  severity: String(item.severity || 'INFO').trim().toLowerCase(),
  title: String(item.title || '').trim(),
  message: String(item.message || '').trim(),
  actionPath: String(item.actionPath || '').trim(),
  actionLabel: String(item.actionLabel || '').trim(),
  createdAt: item.createdAt || new Date().toISOString(),
  isRead: Boolean(item.read ?? item.isRead),
});

const countUnread = (items = []) => items.filter((item) => !item.isRead).length;

export const NotificationsProvider = ({ children }) => {
  const [authState, setAuthState] = useState(() => ({
    user: getUser() || {},
    tenantId: String(getTenant() || '').trim().toLowerCase(),
    isAuthenticated: isAuthenticated(),
  }));

  const { user, tenantId, isAuthenticated: isAuthed } = authState;
  const memberKey = String(user.memberGl || user.customerId || user.username || '').trim().toLowerCase();
  
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const handleAuthChange = () => {
      setAuthState({
        user: getUser() || {},
        tenantId: String(getTenant() || '').trim().toLowerCase(),
        isAuthenticated: isAuthenticated(),
      });
    };

    window.addEventListener('auth-change', handleAuthChange);
    return () => {
      window.removeEventListener('auth-change', handleAuthChange);
    };
  }, []);

  const refreshNotifications = useCallback(async ({ silent = false } = {}) => {
    if (!isAuthenticated()) {
      return;
    }
    if (!silent) {
      setLoading(true);
    }
    setError('');

    try {
      const response = await notificationsAPI.getNotifications({
        page: 0,
        size: 50,
      });
      const payload = parsePayload(response);
      const items = Array.isArray(payload?.notifications)
        ? payload.notifications.map(normalizeNotification).filter((item) => item.id)
        : [];

      setNotifications(items);
      setUnreadCount(Number.isFinite(Number(payload?.unreadCount)) ? Number(payload.unreadCount) : countUnread(items));
      setError('');
    } catch (requestError) {
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthed) {
      refreshNotifications({ silent: true });
    } else {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
    }
  }, [memberKey, tenantId, isAuthed, refreshNotifications]);

  const handleUpdateFailure = useCallback((requestError) => {
    toast.error('Failed to update notification status');
  }, []);

  const markAsRead = useCallback(async (notificationId) => {
    const nextId = String(notificationId || '').trim();
    if (!nextId) return;

    const previousNotifications = notifications;
    const alreadyRead = previousNotifications.find((item) => item.id === nextId)?.isRead;
    const optimisticNotifications = previousNotifications.map((item) => (item.id === nextId ? { ...item, isRead: true } : item));

    setNotifications(optimisticNotifications);
    setUnreadCount(countUnread(optimisticNotifications));

    try {
      await notificationsAPI.updateReadState(nextId, { read: true });
    } catch (requestError) {
      if (!alreadyRead) {
        setNotifications(previousNotifications);
        setUnreadCount(countUnread(previousNotifications));
      }
      handleUpdateFailure(requestError);
    }
  }, [handleUpdateFailure, notifications]);

  const markAsUnread = useCallback(async (notificationId) => {
    const nextId = String(notificationId || '').trim();
    if (!nextId) return;

    const previousNotifications = notifications;
    const alreadyUnread = !previousNotifications.find((item) => item.id === nextId)?.isRead;
    const optimisticNotifications = previousNotifications.map((item) => (item.id === nextId ? { ...item, isRead: false } : item));

    setNotifications(optimisticNotifications);
    setUnreadCount(countUnread(optimisticNotifications));

    try {
      await notificationsAPI.updateReadState(nextId, { read: false });
    } catch (requestError) {
      if (!alreadyUnread) {
        setNotifications(previousNotifications);
        setUnreadCount(countUnread(previousNotifications));
      }
      handleUpdateFailure(requestError);
    }
  }, [handleUpdateFailure, notifications]);

  const markAllAsRead = useCallback(async (notificationIds = []) => {
    const targetIds = (Array.isArray(notificationIds) && notificationIds.length > 0
      ? notificationIds
      : notifications.filter((item) => !item.isRead).map((item) => item.id)
    ).map((item) => String(item || '').trim()).filter(Boolean);

    if (targetIds.length === 0) {
      return;
    }

    const previousNotifications = notifications;
    const optimisticNotifications = previousNotifications.map((item) => (targetIds.includes(item.id) ? { ...item, isRead: true } : item));

    setNotifications(optimisticNotifications);
    setUnreadCount(countUnread(optimisticNotifications));

    try {
      await notificationsAPI.updateReadStateBulk({ notificationIds: targetIds, read: true });
    } catch (requestError) {
      setNotifications(previousNotifications);
      setUnreadCount(countUnread(previousNotifications));
      handleUpdateFailure(requestError);
    }
  }, [handleUpdateFailure, notifications]);

  const priorityNotifications = useMemo(() => {
    return notifications
      .filter((item) => !item.isRead && item.scope === 'member' && ['critical', 'important'].includes(item.severity))
      .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())
      .slice(0, 2);
  }, [notifications]);

  const value = useMemo(() => ({
    notifications,
    unreadCount,
    loading,
    error,
    priorityNotifications,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
    markAsUnread,
  }), [error, loading, markAllAsRead, markAsRead, markAsUnread, notifications, priorityNotifications, refreshNotifications, unreadCount]);

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationsProvider');
  }
  return context;
};

export default NotificationsProvider;
